<template>
  <div
    class="m-multiselect-ac-wrapper"
    :class="{ 'swing-primary-click-mode': hasPrimaryAction && !hasEditor, 'has-editor': hasEditor }"
    @click.capture="handleWrapperClickCapture"
  >
    <AutoComplete
      appendTo="#ui-popup-root"
      v-model="acSelected"
      :suggestions="suggestionsList"
      multiple
      fluid
      :option-label="optionLabelField"
      data-key="__key"
      :loading="suggestLoading"
      :disabled="disabled"
      :invalid="invalid"
      :placeholder="effectivePlaceholder"
      :empty-selection-message="emptySelectionMessage"
      :empty-search-message="emptySearchMessage"
      :min-length="suggestOnFocusGained ? 0 : 1"
      :delay="300"
      :typeahead="true"
      :complete-on-focus="suggestOnFocusGained"
      :class="{ 'p-invalid': invalid }"
      v-bind="$attrs"
      :pt="autocompletePt"
      @complete="onComplete"
      @focus="onFocus"
      @blur="onBlur"
    >
      <template #option="slotProps">
        <span>{{ slotProps.option.__label }}</span>
      </template>
      <template #chip="{ value, index, removeCallback }">
        <Chip
          :label="value.__label"
          :removable="!disabled"
          class="multiselect-chip"
          @remove="onChipRemove(value, index, removeCallback)"
          @contextmenu.prevent="handleChipContextMenu($event, value, index)"
        >
          <template #removeicon="{ removeCallback, keydownCallback }">
            <span
              class="p-chip-remove-icon multiselect-chip-remove-trigger"
              tabindex="0"
              role="button"
              aria-label="Удалить"
              @click.stop="onChipRemove(value, index, removeCallback)"
              @keydown="keydownCallback"
            >
              <i class="pi pi-times-circle" aria-hidden="true" />
            </span>
          </template>
        </Chip>
      </template>
    </AutoComplete>
    <ContextMenu ref="contextMenu" :model="contextMenuModel" @hide="onContextMenuHide" />
  </div>
</template>

<script setup lang="ts">

import { ref, watch, nextTick, computed, onActivated } from 'vue'
import AutoComplete from 'primevue/autocomplete'
import Chip from 'primevue/chip'
import ContextMenu from 'primevue/contextmenu'
import { formatItem, resolveUniDataRowKey, resolveUniDataRowLabel } from '@/components/ui/table/formatters'
import { listboxOptionTextFromRow } from '@/common/utils/listboxTableResolve'
import { activateContextMenu } from '@/common/utils/contextMenuCoordinator'
import { useTableContextMenu } from '@/common/composables/useTableContextMenu'
import { buildStableTestId } from '@/common/utils/testId'
import {
  shouldHoldMultiSelectUserClear,
  sliceLinkedMultiSelectItems,
  resolveMultiSelectSuggestOptions,
} from '@/common/utils/multiSelectLiveItems'

const optionLabelField = '__label'

interface MultiSelectItem {
  label: string
  value: any
  text?: string
  _rowData?: any
}

interface NormalizedMultiSelectItem extends MultiSelectItem {
  __key: string
  __label: string
  __row: any
}

interface AcOption {
  __key: string
  __label: string
  __row: any
}

type MultiselectSuggestOptions = {
  filter?: string
  cursor?: number
  forced?: boolean
}

interface Props {
  modelValue?: any[] | string | null | any
  selectedRowsSource?: any
  items?: MultiSelectItem[]
  placeholder?: string
  invalid?: boolean
  format?: string
  disabled?: boolean
  inlineActions?: any[]
  elementId?: string
  panelId?: string
  contextMenuItems?: any[]
  hasPrimaryAction?: boolean
  /** Режим с полем поиска (multiple + typeahead): плейсхолдер «Поиск...». */
  hasEditor?: boolean
  suggestOnFocusGained?: boolean
  /** Swing linked: removing a chip also drops every chip after it. */
  linked?: boolean
  getSuggestions?: (opts: MultiselectSuggestOptions) => Promise<any[]>
  /** Текст для aria (нет выбранных значений). */
  emptySelectionMessage?: string
  /** Текст пустого списка подсказок. */
  emptySearchMessage?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  selectedRowsSource: undefined,
  items: () => [],
  invalid: false,
  format: undefined,
  disabled: false,
  inlineActions: () => [],
  elementId: undefined,
  panelId: undefined,
  contextMenuItems: () => [],
  hasPrimaryAction: false,
  hasEditor: false,
  suggestOnFocusGained: false,
  linked: false,
  getSuggestions: undefined,
  emptySelectionMessage: 'Ничего не выбрано',
  emptySearchMessage: 'Совпадений не найдено',
})

const emit = defineEmits<{
  'update:modelValue': [value: any[]]
  change: [value: any[]]
  blur: []
  focus: [event: FocusEvent]
  contextmenu: [event: { data: any; target: EventTarget | null; originalEvent: MouseEvent }]
  'primary-click': [event: MouseEvent]
}>()

const chipLabelForCopy = ref<string>('')
const effectiveSelectedValue = computed(() => props.selectedRowsSource ?? props.modelValue)

/** Только при hasEditor — подпись в строке поиска; иначе пусто (подпись поля — в label формы). */
const effectivePlaceholder = computed(() => (props.hasEditor ? 'Поиск...' : ''))

const suggestionsList = ref<AcOption[]>([])

const multiselectListTestId = computed(() => {
  const id = props?.elementId
  if (!id || String(id).trim() === '') return undefined
  return buildStableTestId('element', [id, 'listbox'])
})


const autocompletePt = computed(() => {
  const listTestId = multiselectListTestId.value
  if (!listTestId) return undefined

  return {
    listContainer: { 'data-testid': listTestId },
  }
})

const suggestLoading = ref(false)
let suggestRequestId = 0

const acSelected = ref<AcOption[]>([])
const userClearedLive = ref(false)
const syncingFromParent = ref(false)

const arraysEqual = (arr1: any[], arr2: any[]): boolean => {
  if (arr1.length !== arr2.length) return false
  if (arr1 === arr2) return true
  try {
    return JSON.stringify(arr1) === JSON.stringify(arr2)
  } catch {
    return false
  }
}

const getComparableRow = (input: any): any => {
  if (input == null) return input
  if (typeof input === 'object') {
    if ('__row' in input) return (input as any).__row
    if ('_rowData' in input) return (input as any)._rowData
    if ('value' in input && (input as any).label !== undefined) return (input as any).value
  }
  return input
}

const getStableRowKey = (input: any): string => {
  if (input == null) return ''
  if (typeof input === 'string' || typeof input === 'number' || typeof input === 'boolean') {
    return String(input)
  }
  const comparable = getComparableRow(input)
  return resolveUniDataRowKey(comparable)
}

const valuesEqual = (val1: any, val2: any): boolean => {
  if (val1 === val2) return true
  if (val1 == null || val2 == null) return false
  if (Array.isArray(val1) && Array.isArray(val2)) return arraysEqual(val1, val2)
  if (typeof val1 === 'object' && typeof val2 === 'object') {
    const id1 = getStableRowKey(val1)
    const id2 = getStableRowKey(val2)
    if (id1 && id2 && id1 === id2) return true
    try {
      return JSON.stringify(val1) === JSON.stringify(val2)
    } catch {
      return false
    }
  }
  return false
}

const getValidValues = (values: any[]): any[] => {
  if (!Array.isArray(values)) return []
  const result: any[] = []
  for (const val of values) {
    if (val == null || val === false) continue
    if (typeof val === 'object' && !Array.isArray(val)) {
      if (
        val &&
        (val as any).type === 'UniDataTable' &&
        (val as any).value &&
        typeof (val as any).value === 'object' &&
        Array.isArray((val as any).value.rows)
      ) {
        result.push(...getValidValues((val as any).value.rows))
        continue
      }
      if (Object.keys(val).length === 0) continue
      if (!(val as any).fields && !(val as any).value && !(val as any).label && !(val as any).text) continue
      result.push(val)
    } else if (Array.isArray(val)) {
      if (val.length === 0) continue
      result.push(...getValidValues(val))
    } else {
      result.push(val)
    }
  }
  return result
}

const getValidSelectionKeys = (values: string[]): string[] =>
  values.map((value) => String(value ?? '').trim()).filter(Boolean)

const normalizeModelValue = (val: any): any[] => {
  if (val == null) return []
  if (Array.isArray(val)) return val
  if (typeof val === 'string' && val === '') return []
  if (typeof val === 'object' && Array.isArray(val.rows)) return val.rows
  if (typeof val === 'object' && val.value && Array.isArray(val.value.rows)) return val.value.rows
  if (typeof val === 'object' && val.value && Array.isArray(val.value)) return val.value
  return []
}

const collectNestedTableRows = (input: any, visited = new Set<any>(), depth = 0): any[] => {
  if (input == null || depth > 4) return []
  const unwrapped =
    input && typeof input === 'object' && 'value' in input && !Array.isArray(input)
      ? (input as any).value
      : input
  if (unwrapped == null || typeof unwrapped !== 'object') return []
  if (visited.has(unwrapped)) return []
  visited.add(unwrapped)
  if (Array.isArray(unwrapped)) {
    return unwrapped.flatMap((item) => collectNestedTableRows(item, visited, depth + 1))
  }
  if (Array.isArray((unwrapped as any).rows)) {
    return getValidValues((unwrapped as any).rows)
  }
  return Object.values(unwrapped).flatMap((value) => collectNestedTableRows(value, visited, depth + 1))
}

const dedupeRows = (rows: any[]): any[] => {
  const unique = new Map<string, any>()
  rows.forEach((row, index) => {
    const key = getStableRowKey(row) || `row:${index}`
    if (!unique.has(key)) unique.set(key, row)
  })
  return Array.from(unique.values())
}

const getItemRow = (item: any): any => {
  if (item && typeof item === 'object') {
    if (item._rowData != null) return item._rowData
    if ('value' in item) return item.value
  }
  return item
}

const hasDisplayFormat = computed(() => String(props.format ?? '').trim() !== '')

const formatLabelForRow = (row: any): string => {
  if (row == null) return ''
  if (typeof row !== 'object' || Array.isArray(row)) return String(row)
  if (
    row.type === 'UniDataTable' &&
    row.value &&
    typeof row.value === 'object' &&
    Array.isArray(row.value.rows)
  ) {
    const first = row.value.rows.find((r: any) => r != null)
    if (first != null) return formatLabelForRow(first)
  }
  if (hasDisplayFormat.value) {
    const formatted = formatItem(row, String(props.format).trim())
    if (formatted) return formatted
  }
  return ''
}

const labelForValue = (row: any): string => {
  const formatted = formatLabelForRow(row)
  if (formatted) return formatted
  return listboxOptionTextFromRow(row) || resolveUniDataRowLabel(row)
}

const normalizeSuggestList = (raw: unknown): any[] => {
  if (raw == null) return []
  if (Array.isArray(raw)) return raw
  if (typeof raw === 'object') {
    const v = raw as { rows?: unknown; value?: { rows?: unknown } }
    if (Array.isArray(v.rows)) return v.rows
    if (v.value && typeof v.value === 'object' && Array.isArray((v.value as { rows?: unknown }).rows)) {
      return (v.value as { rows: any[] }).rows
    }
  }
  return []
}

const staticNormalizedItems = computed<NormalizedMultiSelectItem[]>(() =>
  (props.items ?? []).map((item, index) => {
    const row = getItemRow(item)
    const key = getStableRowKey(row) || `row:${index}`
    const formattedLabel = labelForValue(row)
    const fallbackLabel = String(item?.label ?? '').trim() || String(item?.text ?? '').trim()
    const label =
      (hasDisplayFormat.value ? formattedLabel || fallbackLabel : fallbackLabel || formattedLabel) ||
      key
    return {
      ...item,
      label,
      value: row,
      _rowData: row,
      __row: row,
      __key: key,
      __label: label,
    }
  }),
)

const normalizedItemsMap = computed(() => {
  const entries = staticNormalizedItems.value.map((item) => [item.__key, item] as const)
  return new Map<string, NormalizedMultiSelectItem>(entries)
})

const findMatchedNormalizedItem = (row: any): NormalizedMultiSelectItem | undefined => {
  const key = getStableRowKey(row)
  if (key) {
    const direct = normalizedItemsMap.value.get(key)
    if (direct) return direct
  }
  return staticNormalizedItems.value.find((item) => valuesEqual(item.__row, row))
}

const resolveModelRows = (value: any): any[] => {
  const directRows = dedupeRows(getValidValues(normalizeModelValue(value)))
  const nestedRows = dedupeRows(collectNestedTableRows(value))

  if (!directRows.length) {
    const nestedMatchedRows = nestedRows.filter((row) => Boolean(findMatchedNormalizedItem(row)))
    return nestedMatchedRows.length ? dedupeRows([...nestedMatchedRows, ...nestedRows]) : nestedRows
  }
  const directMatchedRows = directRows.filter((row) => Boolean(findMatchedNormalizedItem(row)))
  if (directMatchedRows.length) return dedupeRows([...directMatchedRows, ...directRows])
  const nestedMatchedRows = nestedRows.filter((row) => Boolean(findMatchedNormalizedItem(row)))
  if (nestedMatchedRows.length) return dedupeRows([...nestedMatchedRows, ...nestedRows])
  if (nestedRows.length) return nestedRows
  return directRows
}

const detachedSelectedItems = computed(() => {
  const detached = new Map<string, NormalizedMultiSelectItem>()
  resolveModelRows(effectiveSelectedValue.value).forEach((row, index) => {
    const key = getStableRowKey(row) || `selected:${index}`
    const matched = findMatchedNormalizedItem(row)
    if (matched) return
    if (detached.has(key)) return
    const formattedLabel = formatLabelForRow(row)
    const label =
      (hasDisplayFormat.value ? formattedLabel : formattedLabel || labelForValue(row)) || key
    detached.set(key, {
      label,
      value: row,
      _rowData: row,
      __row: row,
      __key: key,
      __label: label,
    })
  })
  return detached
})

const selectionRegistry = computed(() => {
  const registry = new Map<string, NormalizedMultiSelectItem>()
  staticNormalizedItems.value.forEach((item) => registry.set(item.__key, item))
  detachedSelectedItems.value.forEach((item, key) => {
    if (!registry.has(key)) registry.set(key, item)
  })
  return registry
})

const getSelectionKeysFromModel = (value: any): string[] =>
  resolveModelRows(value)
    .map((row, index) => {
      const matched = findMatchedNormalizedItem(row)
      if (matched) return matched.__key
      const key = getStableRowKey(row) || `selected:${index}`
      return key
    })
    .filter(Boolean)

const resolveRowByKey = (key: string): any =>
  normalizedItemsMap.value.get(key)?.__row ?? selectionRegistry.value.get(key)?.__row

function toAcOption(item: NormalizedMultiSelectItem): AcOption {
  return { __key: item.__key, __label: item.__label, __row: item.__row }
}

function keysToAcOptions(keys: string[]): AcOption[] {
  const out: AcOption[] = []
  for (const key of getValidSelectionKeys(keys)) {
    const reg = selectionRegistry.value.get(key)
    if (reg) out.push(toAcOption(reg))
  }
  return out
}

function acOptionsEqual(a: AcOption[], b: AcOption[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i].__key !== b[i].__key) return false
  }
  return true
}

function filterStaticByQuery(opts: AcOption[], query: string): AcOption[] {
  const q = query.trim().toLowerCase()
  if (!q) return opts
  return opts.filter((o) => o.__label.toLowerCase().includes(q))
}

const staticAcOptions = computed(() => staticNormalizedItems.value.map(toAcOption))

function syncAcFromParent() {
  const keys = getSelectionKeysFromModel(effectiveSelectedValue.value)
  if (shouldHoldMultiSelectUserClear(userClearedLive.value, keys.length)) {
    return
  }
  if (userClearedLive.value && keys.length === 0) {
    userClearedLive.value = false
  }
  syncingFromParent.value = true
  try {
    const next = keysToAcOptions(keys)
    if (!acOptionsEqual(acSelected.value, next)) {
      acSelected.value = next
    }
  } finally {
    // Сбрасываем флаг синхронно, а не в nextTick
    syncingFromParent.value = false
  }
}

watch(
  [effectiveSelectedValue, staticNormalizedItems],
  () => {
    syncAcFromParent()
  },
  { immediate: true, deep: true },
)

onActivated(() => {
  nextTick(() => {
    suggestionsList.value = []
    syncAcFromParent()
  })
})

watch(acSelected, (newVal, oldVal) => {
  const isUserAction = !syncingFromParent.value || (oldVal && newVal.length !== oldVal.length)

  if (!isUserAction) {
    return
  }

  const rows = newVal.map((t) => t.__row).filter((r) => r != null)
  const prevKeys = getSelectionKeysFromModel(effectiveSelectedValue.value)
  const newKeys = newVal.map((t) => t.__key)

  if (!arraysEqual(prevKeys, newKeys)) {
    if ((oldVal?.length ?? 0) > 0 && newVal.length === 0) {
      userClearedLive.value = true
    } else if (newVal.length > 0) {
      userClearedLive.value = false
    }
    emit('update:modelValue', rows)
    emit('change', rows)
  }
}, { deep: true })

const runCompleteSearch = async (event: { query?: string }) => {
  const query = event.query ?? ''
  if (!props.suggestOnFocusGained && !String(query).trim()) {
    suggestionsList.value = []
    return
  }

  if (!props.getSuggestions) {
    suggestionsList.value = resolveMultiSelectSuggestOptions({
      serverRows: [],
      staticRows: filterStaticByQuery(staticAcOptions.value, query),
      hasServerFetcher: false,
    })
    return
  }

  suggestLoading.value = true
  const rid = ++suggestRequestId
  try {
    const list = await props.getSuggestions({
      filter: query,
      cursor: query.length,
      forced: false,
    })
    if (rid !== suggestRequestId) return
    const rows = normalizeSuggestList(list)
    const serverOpts: AcOption[] = rows.map((row, i) => {
      const key = getStableRowKey(row) || `srv:${i}`
      const __label = labelForValue(row) || key
      return { __key: key, __label, __row: row }
    })
    suggestionsList.value = resolveMultiSelectSuggestOptions({
      serverRows: serverOpts,
      staticRows: filterStaticByQuery(staticAcOptions.value, query),
      hasServerFetcher: true,
    })
  } catch {
    if (rid !== suggestRequestId) return
    suggestionsList.value = resolveMultiSelectSuggestOptions({
      serverRows: [],
      staticRows: filterStaticByQuery(staticAcOptions.value, query),
      hasServerFetcher: true,
    })
  } finally {
    if (rid === suggestRequestId) suggestLoading.value = false
  }
}

function onComplete(event: { originalEvent: Event; query: string }) {
  void runCompleteSearch(event)
}

watch(
  () => props.items,
  () => {
    suggestionsList.value = []
  },
  { deep: true },
)

const {
  contextMenu,
  contextMenuModel,
  handleContextMenu: handleTableContextMenu,
  onContextMenuHide,
  contextCellValue,
  contextRowData,
} = useTableContextMenu({
  contextMenuItems: computed(() => props.contextMenuItems || []),
  headers: computed(() => []),
  onEmit: (event) => {
    emit('contextmenu', {
      data: event.data,
      target: event.target,
      originalEvent: event.target as unknown as MouseEvent,
    })
  },
})

const getLabelForKey = (key: string) => {
  const matched = normalizedItemsMap.value.get(key)
  if (matched) {
    const formatted = formatLabelForRow(matched.__row)
    if (formatted) return formatted
    return matched.__label
  }
  const detached = selectionRegistry.value.get(key)
  if (!detached) return key
  const formatted = formatLabelForRow(detached.__row)
  if (formatted) return formatted
  return hasDisplayFormat.value ? '' : detached.__label
}

function handleChipContextMenu(event: MouseEvent, token: AcOption, index: number) {
  event.preventDefault()
  event.stopPropagation()
  const value = token.__row
  if (value == null) return
  const chipLabel =
    token.__label || getLabelForKey(token.__key) || labelForValue(value)
  let rowData = value
  if (rowData && typeof rowData === 'object') {
    rowData = { ...rowData, _chipLabel: chipLabel }
  }
  contextRowData.value = rowData
  emit('contextmenu', {
    data: rowData,
    target: event.target,
    originalEvent: event,
  })
  chipLabelForCopy.value = chipLabel
  handleTableContextMenu(event, rowData, '')
  Promise.resolve().then(() => {
    contextCellValue.value = chipLabelForCopy.value
  })
  nextTick(() => {
    if (contextMenu.value && contextMenuModel.value.length > 0) {
      if (!document.querySelector('.p-contextmenu')) {
        const t = event.target
        if (t instanceof Element && document.contains(t)) {
          activateContextMenu(contextMenu.value, event.target)
          contextMenu.value.show(event)
        }
      }
    }
  })
}

const shouldBypassPrimaryClick = (target: EventTarget | null): boolean => {
  const element = target instanceof Element ? target : null
  if (!element) return false
  return Boolean(
    element.closest(
      [
        '.multiselect-chip',
        '.multiselect-chip-remove-trigger',
        '.p-chip',
        '.pc-chip',
        '.p-chip-remove-icon',
        '.p-contextmenu',
        // Не добавлять ul[role="listbox"] и контейнер ячейки ввода: в PrimeVue 4 multiple
        // весь ряд chips+input — это ul[role="listbox"], а input внутри li.inputChip —
        // иначе любой клик по полю ошибочно обходится и primary-click не сработает.
        'button',
        'a',
      ].join(', '),
    ),
  )
}

function onChipRemove(_value: AcOption, index: number, removeCallback: () => void) {
  suggestionsList.value = []
  if (props.linked) {
    acSelected.value = sliceLinkedMultiSelectItems(acSelected.value, index)
    return
  }
  removeCallback()
}

function handleWrapperClickCapture(event: MouseEvent) {
  const bypass = shouldBypassPrimaryClick(event.target)
  if (props.hasPrimaryAction && !props.hasEditor && !props.disabled) {
    if (!bypass) {
      event.preventDefault()
      event.stopPropagation()
      emit('primary-click', event)
    }
  }
}

function onFocus(e: FocusEvent) {
  emit('focus', e)
}

function onBlur() {
  emit('blur')
}

function getLiveSelectedRows(): unknown[] {
  return acSelected.value.map((token) => token.__row).filter((row) => row != null)
}

defineExpose({
  getLiveSelectedRows,
})
</script>

<style scoped>
.m-multiselect-ac-wrapper {
  width: 100%;
  min-width: 0;
}

.m-multiselect-ac-wrapper :deep(.p-autocomplete) {
  width: 100%;
  min-width: 0;
}

.m-multiselect-ac-wrapper :deep(.p-autocomplete-input-multiple) {
  overflow: visible;
  min-width: 0;
  box-sizing: border-box;
}

.m-multiselect-ac-wrapper :deep(.p-autocomplete-chip-item),
.m-multiselect-ac-wrapper :deep(.p-chip),
.m-multiselect-ac-wrapper :deep(.multiselect-chip) {
  flex-shrink: 0;
  max-width: 100%;
}

.m-multiselect-ac-wrapper :deep(.p-autocomplete-input-chip) {
  flex: 1 1 6rem;
  min-width: 6rem;
}

.m-multiselect-ac-wrapper:not(.has-editor) :deep(.p-autocomplete-input-chip) {
  display: none;
}

.m-multiselect-ac-wrapper :deep(.p-autocomplete-input-chip input) {
  width: 100%;
  min-width: 0;
  height: auto;
  min-height: 0;
  border: 0 none;
  border-radius: 0;
  box-shadow: none;
  background: transparent;
}

.multiselect-chip {
  cursor: context-menu;
}
</style>
